"use client";

import {
  Activity,
  ArrowRight,
  Battery,
  Cable,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  CloudUpload,
  Cpu,
  Database,
  Loader2,
  Play,
  Radio,
  RefreshCw,
  SignalHigh,
  SignalLow,
  SignalMedium,
  TerminalSquare,
  Wifi,
  WifiOff,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { apiClient } from "@/lib/api/client";
import { cn } from "@/lib/utils/cn";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export type ConnectionHealth = "online" | "degraded" | "offline" | "unknown";

export type ConnectionStatusItem = {
  connectionId: string;
  label: string;
  connectionType: string;
  health: ConnectionHealth;
  lastSeenAt: string | null;
  firmwareVersion?: string | null;
  ipAddress?: string | null;
  pendingBufferCount?: number;
  batchAvailable?: boolean;
};

type DrainStatus = "idle" | "running" | "success" | "error";

type DrainLog = {
  id: string;
  timestamp: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
};

type Props = {
  deviceId: string;
  deviceUid: string;
  connections: ConnectionStatusItem[];
  onTriggerBatchPush?: (connectionId: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  isLoading?: boolean;
  className?: string;
};

type BluetoothSerialEvent = {
  connection_id: string;
  device_id: string;
  device_uid: string;
  device_name?: string | null;
  port?: string | null;
  baud_rate?: number | null;
  state: string;
  message: string;
  at?: string;
  updated_at?: string;
  last_line?: string;
  last_payload?: Record<string, unknown>;
  last_error?: string | null;
  last_read_at?: string;
  last_ingested_at?: string;
  next_allowed_at?: string;
  readings_ingested?: number;
};

type BluetoothSerialStatusResponse = {
  items?: BluetoothSerialEvent[];
  events?: BluetoothSerialEvent[];
};

type WebSerialLog = {
  id: string;
  at: string;
  state: "info" | "success" | "warning" | "error";
  message: string;
};

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return "Never";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 5) return "Just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function bluetoothStateStyle(state: string): string {
  switch (state) {
    case "ingested":
      return "text-success";
    case "reading":
    case "connected":
      return "text-primary";
    case "throttled":
      return "text-warning";
    case "error":
    case "rejected":
      return "text-error";
    default:
      return "text-muted";
  }
}

function formatBluetoothEventLine(event: BluetoothSerialEvent): string {
  const stamp = event.at ?? event.updated_at ?? new Date().toISOString();
  const time = new Date(stamp).toLocaleTimeString();
  const parts = [`[${time}]`, event.state.toUpperCase(), "-", event.message];
  if (event.port) parts.push(`(${event.port})`);
  if (event.last_line) parts.push(`line="${event.last_line}"`);
  if (event.last_payload) parts.push(`payload=${JSON.stringify(event.last_payload)}`);
  if (event.last_error) parts.push(`error="${event.last_error}"`);
  return parts.join(" ");
}

function parseBluetoothSerialLineForBrowser(line: string): Record<string, number> {
  const out: Record<string, number> = {};
  const pairRe = /([A-Za-z][A-Za-z0-9_ -]{0,63})\s*[:=]\s*([-+]?\d+(?:\.\d+)?)/g;
  for (const match of line.matchAll(pairRe)) {
    const rawKey = String(match[1] ?? "").trim().toLowerCase();
    const value = Number(match[2]);
    if (!rawKey || !Number.isFinite(value)) continue;
    out[rawKey] = value;
  }
  return out;
}

function webSerialLogStyle(state: WebSerialLog["state"]): string {
  if (state === "success") return "text-success";
  if (state === "warning") return "text-warning";
  if (state === "error") return "text-error";
  return "text-white/55";
}

function serialSetupAdvice(message: string | null): string {
  const raw = (message ?? "").toLowerCase();
  if (raw.includes("no port selected") || raw.includes("no compatible") || raw.includes("notfound")) {
    return "No serial port was selected. Turn on Bluetooth, pair ESP32_SHT40 in Windows, confirm the Outgoing COM port exists, then try again.";
  }
  if (raw.includes("access denied") || raw.includes("networkerror") || raw.includes("already open")) {
    return "The COM port may already be open. Close FastAPI Bluetooth consumer, Arduino Serial Monitor, PuTTY, or any other serial app, then try again.";
  }
  if (raw.includes("security") || raw.includes("permission")) {
    return "Browser permission was blocked. Use Chrome or Edge on localhost/HTTPS and allow serial port access.";
  }
  return "If no port appears, turn on Bluetooth, pair ESP32_SHT40, and verify Windows Bluetooth Settings shows an Outgoing COM port.";
}

function getHealthCfg(health: ConnectionHealth) {
  switch (health) {
    case "online":
      return {
        label: "ONLINE",
        textColor: "text-success",
        bg: "bg-success/10",
        border: "border-success/25",
        dot: "bg-success",
        bar: "bg-success",
        pulse: true,
        SignalIcon: SignalHigh,
        headerBg: "bg-success/10",
      };
    case "degraded":
      return {
        label: "DEGRADED",
        textColor: "text-warning",
        bg: "bg-warning/10",
        border: "border-warning/25",
        dot: "bg-warning",
        bar: "bg-warning",
        pulse: true,
        SignalIcon: SignalMedium,
        headerBg: "bg-warning/10",
      };
    case "offline":
      return {
        label: "OFFLINE",
        textColor: "text-error",
        bg: "bg-error/10",
        border: "border-error/25",
        dot: "bg-error",
        bar: "bg-error",
        pulse: false,
        SignalIcon: SignalLow,
        headerBg: "bg-error/10",
      };
    default:
      return {
        label: "UNKNOWN",
        textColor: "text-muted",
        bg: "bg-muted-bg/35",
        border: "border-border/60",
        dot: "bg-muted",
        bar: "bg-muted/50",
        pulse: false,
        SignalIcon: SignalLow,
        headerBg: "bg-muted-bg/30",
      };
  }
}

function connTypeIcon(type: string) {
  const t = type.toLowerCase();
  if (t.includes("wifi")) return <Wifi className="h-3.5 w-3.5" />;
  if (t.includes("mqtt")) return <Radio className="h-3.5 w-3.5" />;
  if (t.includes("http")) return <Zap className="h-3.5 w-3.5" />;
  if (t.includes("lora")) return <Activity className="h-3.5 w-3.5" />;
  return <Cpu className="h-3.5 w-3.5" />;
}

function PulsingDot({ color, active }: { color: string; active: boolean }) {
  return (
    <span className="relative flex h-2.5 w-2.5 shrink-0">
      {active ? <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-40", color)} /> : null}
      <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", color)} />
    </span>
  );
}

function MetaCell({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted">
        {icon}
        {label}
      </div>
      <p className="mt-1 truncate text-xs font-semibold text-foreground">{value}</p>
    </div>
  );
}

function QueueDrainPanel({
  count,
  connectionId,
  onDrain,
}: {
  count: number;
  connectionId: string;
  onDrain?: (connectionId: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<DrainStatus>("idle");
  const [logs, setLogs] = useState<DrainLog[]>([]);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(count);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const addLog = (message: string, type: DrainLog["type"]) => {
    setLogs((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date().toLocaleTimeString(),
        message,
        type,
      },
    ]);
  };

  const handleDrain = useCallback(async () => {
    if (!onDrain) return;
    setStatus("running");
    setLogs([]);
    setProcessed(0);
    setTotal(count);

    addLog(`Starting buffer drain - ${count} readings queued`, "info");
    addLog(`Connection: ${connectionId}`, "info");

    try {
      await new Promise((r) => setTimeout(r, 400));
      addLog("Connecting to buffer service...", "info");

      await new Promise((r) => setTimeout(r, 500));
      addLog("Verifying database availability...", "info");

      await new Promise((r) => setTimeout(r, 300));
      addLog("Initiating batch flush to PostgreSQL...", "info");

      const batchSize = Math.ceil(count / 3);
      for (let i = 1; i <= 3; i += 1) {
        await new Promise((r) => setTimeout(r, 600));
        const done = Math.min(i * batchSize, count);
        setProcessed(done);
        addLog(`Batch ${i}/3 - ${done}/${count} records flushed`, "info");
      }

      await onDrain(connectionId);

      await new Promise((r) => setTimeout(r, 300));
      addLog(`All ${count} readings persisted to database`, "success");
      addLog("Redis buffer cleared successfully", "success");
      addLog("Drain complete - queue depth: 0", "success");
      setProcessed(count);
      setStatus("success");
    } catch (err) {
      addLog(`Error: ${err instanceof Error ? err.message : "Unknown failure"}`, "error");
      addLog("Drain aborted - some records may remain in buffer", "warning");
      setStatus("error");
    }
  }, [connectionId, count, onDrain]);

  const logTypeStyle: Record<DrainLog["type"], string> = {
    info: "text-muted",
    success: "text-success",
    error: "text-error",
    warning: "text-warning",
  };

  const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-warning/25 bg-warning/10">
      <div className="flex items-center gap-3 border-b border-warning/20 bg-card/70 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-warning/25 bg-warning/15 text-warning">
          <Database className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Buffer Queue Active</p>
          <p className="text-[11px] text-muted">{count} readings awaiting database flush</p>
        </div>
        <span className="shrink-0 rounded-full border border-warning/25 bg-warning/15 px-2.5 py-0.5 text-xs font-bold text-warning">
          {count}
        </span>
      </div>

      <div className="space-y-3 px-4 py-3">
        {status !== "idle" ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] text-muted">
              <span>{processed}/{total} records</span>
              <span className="font-semibold text-foreground">{progressPct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted-bg">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  status === "success" ? "bg-success" : status === "error" ? "bg-error" : "bg-primary",
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        ) : null}

        {logs.length > 0 ? (
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border/60 bg-foreground px-3 py-2.5">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start gap-2 text-[11px] leading-relaxed">
                <span className="shrink-0 text-white/35">[{log.timestamp}]</span>
                <span className={logTypeStyle[log.type]}>{log.message}</span>
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        ) : null}

        {status === "success" ? (
          <div className="flex items-center gap-2 rounded-lg border border-success/25 bg-success/10 px-3 py-2">
            <CheckCheck className="h-4 w-4 shrink-0 text-success" />
            <p className="text-xs font-semibold text-success">Drain complete - all records persisted</p>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="flex items-center gap-2 rounded-lg border border-error/25 bg-error/10 px-3 py-2">
            <XCircle className="h-4 w-4 shrink-0 text-error" />
            <p className="text-xs font-semibold text-error">Drain failed - check logs above</p>
          </div>
        ) : null}

        {status === "idle" || status === "error" ? (
          <button
            onClick={handleDrain}
            disabled={!onDrain}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-warning/25 bg-warning px-4 py-2 text-sm font-semibold text-white transition hover:opacity-95 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Play className="h-3.5 w-3.5" />
            {status === "error" ? "Retry Drain" : "Process Queue Now"}
            <ArrowRight className="ml-auto h-3.5 w-3.5 opacity-60" />
          </button>
        ) : null}

        {status === "running" ? (
          <div className="flex items-center justify-center gap-2 rounded-lg border border-primary/15 bg-primary/10 px-4 py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            <span className="text-sm font-semibold text-primary">Processing...</span>
          </div>
        ) : null}

        {status === "success" ? (
          <button
            onClick={() => {
              setStatus("idle");
              setLogs([]);
              setProcessed(0);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 bg-card px-4 py-2 text-sm font-medium text-muted transition hover:border-border hover:bg-muted-bg/20"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ConnectionRow({
  conn,
  onBatchPush,
}: {
  conn: ConnectionStatusItem;
  onBatchPush?: (id: string) => Promise<void>;
}) {
  const cfg = getHealthCfg(conn.health);
  const [expanded, setExpanded] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [relTime, setRelTime] = useState(() => formatRelativeTime(conn.lastSeenAt));

  useEffect(() => {
    const id = setInterval(() => setRelTime(formatRelativeTime(conn.lastSeenAt)), 5000);
    return () => clearInterval(id);
  }, [conn.lastSeenAt]);

  const handleBatch = useCallback(async () => {
    if (!onBatchPush) return;
    setPushing(true);
    try {
      await onBatchPush(conn.connectionId);
    } finally {
      setPushing(false);
    }
  }, [conn.connectionId, onBatchPush]);

  return (
    <div className={cn("overflow-hidden rounded-xl border bg-card shadow-card transition-all duration-200", cfg.border)}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-all hover:brightness-[0.99]", cfg.headerBg)}
        aria-expanded={expanded}
      >
        <PulsingDot color={cfg.dot} active={cfg.pulse} />
        <span className={cn("shrink-0", cfg.textColor)}>{connTypeIcon(conn.connectionType)}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{conn.label}</span>
        <span className="hidden shrink-0 rounded border border-border/60 bg-card px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted sm:inline">
          {conn.connectionType}
        </span>
        <span className={cn("flex shrink-0 items-center gap-1.5 text-[11px] font-bold tracking-widest", cfg.textColor)}>
          <cfg.SignalIcon className="h-3.5 w-3.5" />
          {cfg.label}
        </span>
        <span className="hidden shrink-0 items-center gap-1 text-xs text-muted sm:flex">
          <Clock className="h-3 w-3" />
          {relTime}
        </span>
        {(conn.pendingBufferCount ?? 0) > 0 ? (
          <span className="shrink-0 rounded-full border border-warning/25 bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold text-warning">
            {conn.pendingBufferCount}
          </span>
        ) : null}
        <span className="shrink-0 text-muted">{expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>

      <div className="h-[3px] w-full bg-muted-bg/60">
        <div
          className={cn(
            "h-full transition-all duration-700",
            cfg.bar,
            conn.health === "online" ? "w-full" : conn.health === "degraded" ? "w-2/3" : conn.health === "offline" ? "w-1/6" : "w-0",
          )}
        />
      </div>

      {expanded ? (
        <div className="space-y-3 border-t border-border/50 px-4 py-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {conn.firmwareVersion ? <MetaCell icon={<Cpu className="h-3 w-3" />} label="Firmware" value={conn.firmwareVersion} /> : null}
            {conn.ipAddress ? <MetaCell icon={<Radio className="h-3 w-3" />} label="IP Address" value={conn.ipAddress} /> : null}
            <MetaCell
              icon={<Clock className="h-3 w-3" />}
              label="Last Heartbeat"
              value={conn.lastSeenAt ? new Date(conn.lastSeenAt).toLocaleTimeString() : "-"}
            />
          </div>

          {(conn.pendingBufferCount ?? 0) > 0 ? (
            <QueueDrainPanel count={conn.pendingBufferCount ?? 0} connectionId={conn.connectionId} onDrain={onBatchPush} />
          ) : null}

          {conn.batchAvailable && (conn.pendingBufferCount ?? 0) === 0 ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/15 bg-primary/10 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <CloudUpload className="h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-xs font-semibold text-primary">Offline Data Ready</p>
                  <p className="text-[11px] text-muted">Reconnected - batch sync available</p>
                </div>
              </div>
              <button
                onClick={handleBatch}
                disabled={pushing}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-primary/15 bg-primary px-3 py-1.5 text-xs font-bold text-white transition hover:opacity-95 disabled:opacity-50"
              >
                {pushing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />}
                {pushing ? "Syncing..." : "Sync Now"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SummaryChip({
  value,
  label,
  color,
  bg,
  border,
}: {
  value: number;
  label: string;
  color: string;
  bg: string;
  border: string;
}) {
  return (
    <div className={cn("flex items-center gap-2 rounded-lg border px-3 py-1.5", border, bg)}>
      <span className={cn("text-sm font-bold tabular-nums", color)}>{value}</span>
      <span className="text-xs text-muted">{label}</span>
    </div>
  );
}

export function DataloggerConnectionStatus({
  deviceUid,
  connections,
  onTriggerBatchPush,
  onRefresh,
  isLoading = false,
  className = "",
}: Props) {
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [onRefresh]);

  const online = connections.filter((c) => c.health === "online").length;
  const degraded = connections.filter((c) => c.health === "degraded").length;
  const offline = connections.filter((c) => c.health === "offline").length;
  const totalBuf = connections.reduce((s, c) => s + (c.pendingBufferCount ?? 0), 0);
  const allOnline = online === connections.length && connections.length > 0;

  return (
    <div className={cn("relative overflow-hidden rounded-2xl border border-card-border bg-card shadow-card", className)}>
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-[0.12] blur-3xl"
        style={{ background: allOnline ? "var(--success)" : totalBuf > 0 ? "var(--warning)" : "var(--mesh-blue)" }}
        aria-hidden
      />

      <div className="flex items-center justify-between gap-4 border-b border-card-border bg-muted-bg/35 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-primary/15 bg-primary/10">
            <TerminalSquare className="h-4 w-4 text-primary" />
            <span
              className={cn(
                "absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card",
                allOnline ? "bg-success" : offline === connections.length ? "bg-error" : "bg-warning",
              )}
            />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight text-foreground">Connection Status</h3>
            <p className="text-[10px] uppercase tracking-widest text-muted">{deviceUid}</p>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing || isLoading}
          className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs font-medium text-muted shadow-card transition hover:border-border hover:bg-muted-bg/20 disabled:opacity-40"
        >
          <RefreshCw className={cn("h-3 w-3", (refreshing || isLoading) && "animate-spin")} />
          Refresh
        </button>
      </div>

      {!isLoading && connections.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border/50 bg-card px-5 py-3">
          <SummaryChip value={online} label="Online" color="text-success" bg="bg-success/10" border="border-success/25" />
          {degraded > 0 ? <SummaryChip value={degraded} label="Degraded" color="text-warning" bg="bg-warning/10" border="border-warning/25" /> : null}
          {offline > 0 ? <SummaryChip value={offline} label="Offline" color="text-error" bg="bg-error/10" border="border-error/25" /> : null}
          {totalBuf > 0 ? (
            <div className="ml-auto flex items-center gap-1.5 rounded-lg border border-warning/25 bg-warning/10 px-3 py-1.5">
              <Battery className="h-3.5 w-3.5 text-warning" />
              <span className="text-xs font-bold text-warning">{totalBuf}</span>
              <span className="text-xs text-muted">buffered</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2.5 bg-muted-bg/25 px-5 pb-4 pt-3">
        {isLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl border border-card-border bg-muted-bg/60" />
          ))
        ) : connections.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-card-border bg-card shadow-card">
              <WifiOff className="h-5 w-5 text-muted" />
            </div>
            <div>
              <p className="text-sm font-semibold text-muted">No connections configured</p>
              <p className="mt-0.5 text-xs text-muted">Add a connection to start monitoring</p>
            </div>
          </div>
        ) : (
          connections.map((conn) => (
            <ConnectionRow key={conn.connectionId} conn={conn} onBatchPush={onTriggerBatchPush} />
          ))
        )}
      </div>

      {!isLoading && connections.length > 0 ? (
        <div className="flex items-center justify-between border-t border-card-border bg-muted-bg/35 px-5 py-2.5">
          <span className="text-[10px] uppercase tracking-widest text-muted">Heartbeat Monitor Active</span>
          <span className="text-[10px] text-muted">{new Date().toLocaleTimeString()}</span>
        </div>
      ) : null}
    </div>
  );
}

export function BluetoothSerialTerminal({ deviceId, className = "" }: { deviceId: string; className?: string }) {
  const [events, setEvents] = useState<BluetoothSerialEvent[]>([]);
  const [latest, setLatest] = useState<BluetoothSerialEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get<BluetoothSerialStatusResponse>("/connections/bluetooth-serial/status", {
        params: { device_id: deviceId },
        __skipTenantHeader: true,
      });
      if (!mountedRef.current) return;
      setLatest(data.items?.[0] ?? null);
      setEvents(data.events ?? []);
      setLastUpdatedAt(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load Bluetooth serial status");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    mountedRef.current = true;
    void fetchStatus();
    const id = window.setInterval(() => {
      void fetchStatus();
    }, 2500);
    return () => {
      mountedRef.current = false;
      window.clearInterval(id);
    };
  }, [fetchStatus]);

  useEffect(() => {
    const el = logContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events]);

  const latestState = latest?.state ?? "waiting";
  const terminalLines =
    events.length > 0
      ? events.map((event) => ({
          id: `${event.at ?? event.updated_at ?? event.state}-${event.state}-${event.last_line ?? ""}`,
          event,
          line: formatBluetoothEventLine(event),
        }))
      : [];

  return (
    <div className={cn("overflow-hidden rounded-2xl border border-card-border bg-card shadow-card", className)}>
      <div className="flex flex-col gap-3 border-b border-card-border bg-muted-bg/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
            <TerminalSquare className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight text-foreground">Bluetooth Serial Terminal</h3>
            <p className="text-[10px] uppercase tracking-widest text-muted">
              {latest?.port ?? "Waiting for COM port"} {latest?.baud_rate ? `/ ${latest.baud_rate} baud` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("rounded-lg border border-border/60 bg-card px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest", bluetoothStateStyle(latestState))}>
            {loading ? "Loading" : latestState}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-muted">
            Auto refresh {lastUpdatedAt ? `- ${lastUpdatedAt}` : ""}
          </span>
          <button
            type="button"
            onClick={() => void fetchStatus()}
            className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs font-medium text-muted shadow-card transition hover:border-border hover:bg-muted-bg/20"
          >
            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      <div className="border-b border-border/50 bg-card px-5 py-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <MetaCell icon={<Radio className="h-3 w-3" />} label="Device" value={latest?.device_name || latest?.device_uid || "-"} />
          <MetaCell icon={<Clock className="h-3 w-3" />} label="Last Read" value={latest?.last_read_at ? new Date(latest.last_read_at).toLocaleTimeString() : "-"} />
          <MetaCell icon={<CheckCheck className="h-3 w-3" />} label="Ingested" value={String(latest?.readings_ingested ?? 0)} />
        </div>
      </div>

      <div className="bg-[#07111f] px-4 py-3">
        <div
          ref={logContainerRef}
          className="max-h-72 min-h-40 overflow-y-auto rounded-xl border border-white/10 bg-[#050b14] px-3 py-2.5 font-mono text-[11px] leading-relaxed shadow-inner"
        >
          {error ? <p className="text-error">[error] {error}</p> : null}
          {!error && terminalLines.length === 0 ? (
            <p className="text-white/45">[{new Date().toLocaleTimeString()}] Waiting for Bluetooth serial activity...</p>
          ) : null}
          {terminalLines.map(({ id, event, line }, index) => (
            <p key={`${id}-${index}`} className={cn("break-words", bluetoothStateStyle(event.state))}>
              {line}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BluetoothWebSerialBridge({
  deviceId,
  apiKey,
  baudRate = 115200,
  className = "",
}: {
  deviceId: string;
  apiKey: string | null;
  baudRate?: number;
  className?: string;
}) {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [logs, setLogs] = useState<WebSerialLog[]>([]);
  const [lastPayload, setLastPayload] = useState<Record<string, number> | null>(null);
  const [postedCount, setPostedCount] = useState(0);
  const [connectIssue, setConnectIssue] = useState<string | null>(null);
  const portRef = useRef<SerialPort | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const stopRef = useRef(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const serialSupported = typeof navigator !== "undefined" && Boolean(navigator.serial);

  const addLog = useCallback((message: string, state: WebSerialLog["state"] = "info") => {
    setLogs((current) => [
      ...current.slice(-120),
      {
        id: `${Date.now()}-${Math.random()}`,
        at: new Date().toLocaleTimeString(),
        state,
        message,
      },
    ]);
  }, []);

  useEffect(() => {
    const el = logContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [logs]);

  const postPayload = useCallback(
    async (line: string, payload: Record<string, number>) => {
      if (!apiKey) {
        addLog(`missing provisioning api key; skipped line="${line}"`, "warning");
        return;
      }
      try {
        await apiClient.post("/telemetry/sync", {
          device_id: deviceId,
          api_keys: apiKey,
          time: new Date().toISOString(),
          data: payload,
        });
        setPostedCount((count) => count + 1);
        setLastPayload(payload);
        addLog(`posted line="${line}" payload=${JSON.stringify(payload)}`, "success");
      } catch (err) {
        addLog(`post failed line="${line}" error="${err instanceof Error ? err.message : "unknown error"}"`, "error");
      }
    },
    [addLog, apiKey, deviceId],
  );

  const readLoop = useCallback(
    async (port: SerialPort) => {
      if (!port.readable) {
        addLog("selected serial port is not readable", "error");
        return;
      }
      const decoder = new TextDecoder();
      let buffer = "";
      readerRef.current = port.readable.getReader();
      addLog("reader started", "success");
      try {
        while (!stopRef.current) {
          const { value, done } = await readerRef.current.read();
          if (done) break;
          if (!value) continue;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split(/\r?\n/);
          buffer = parts.pop() ?? "";
          for (const rawLine of parts) {
            const line = rawLine.trim();
            if (!line) continue;
            const payload = parseBluetoothSerialLineForBrowser(line);
            if (Object.keys(payload).length === 0) {
              addLog(`unparsed line="${line}"`, "warning");
              continue;
            }
            addLog(`read line="${line}" payload=${JSON.stringify(payload)}`, "info");
            await postPayload(line, payload);
          }
        }
      } catch (err) {
        if (!stopRef.current) {
          addLog(`read error="${err instanceof Error ? err.message : "unknown error"}"`, "error");
        }
      } finally {
        readerRef.current?.releaseLock();
        readerRef.current = null;
        setConnected(false);
        addLog("reader stopped", "warning");
      }
    },
    [addLog, postPayload],
  );

  const connect = useCallback(async () => {
    if (!navigator.serial || connecting || connected) return;
    setConnecting(true);
    setConnectIssue(null);
    stopRef.current = false;
    try {
      addLog("opening browser serial picker", "info");
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate });
      portRef.current = port;
      setConnected(true);
      addLog(`port opened at ${baudRate} baud`, "success");
      void readLoop(port);
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error";
      setConnectIssue(message);
      addLog(`connect failed="${message}"`, "error");
    } finally {
      setConnecting(false);
    }
  }, [addLog, baudRate, connected, connecting, readLoop]);

  const disconnect = useCallback(async () => {
    stopRef.current = true;
    try {
      await readerRef.current?.cancel();
    } catch {
      /* ignore */
    }
    try {
      await portRef.current?.close();
    } catch {
      /* ignore */
    }
    portRef.current = null;
    setConnected(false);
    addLog("port closed", "warning");
  }, [addLog]);

  useEffect(() => {
    return () => {
      stopRef.current = true;
      void readerRef.current?.cancel();
      void portRef.current?.close();
    };
  }, []);

  return (
    <div className={cn("overflow-hidden rounded-2xl border border-card-border bg-card shadow-card", className)}>
      <div className="flex flex-col gap-3 border-b border-card-border bg-muted-bg/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
            <Cable className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight text-foreground">Browser Serial Bridge</h3>
            <p className="text-[10px] uppercase tracking-widest text-muted">
              {serialSupported ? `${baudRate} baud / posts to telemetry sync` : "web serial unavailable"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("rounded-lg border border-border/60 bg-card px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest", connected ? "text-success" : "text-muted")}>
            {connected ? "connected" : connecting ? "connecting" : "idle"}
          </span>
          <button
            type="button"
            onClick={connected ? () => void disconnect() : () => void connect()}
            disabled={!serialSupported || connecting || !apiKey}
            className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs font-medium text-muted shadow-card transition hover:border-border hover:bg-muted-bg/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : connected ? <XCircle className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            {connected ? "Disconnect" : "Select COM Port"}
          </button>
        </div>
      </div>

      <div className="border-b border-border/50 bg-card px-5 py-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <MetaCell icon={<CheckCheck className="h-3 w-3" />} label="Posted" value={String(postedCount)} />
          <MetaCell icon={<Clock className="h-3 w-3" />} label="Mode" value="Client side" />
          <MetaCell icon={<Radio className="h-3 w-3" />} label="Last Payload" value={lastPayload ? JSON.stringify(lastPayload) : "-"} />
        </div>
      </div>

      <div className="border-b border-border/50 bg-muted-bg/20 px-5 py-3">
        <div className={cn("rounded-xl border px-4 py-3", connectIssue ? "border-warning/30 bg-warning/10" : "border-primary/15 bg-primary/5")}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <p className={cn("text-xs font-bold uppercase tracking-widest", connectIssue ? "text-warning" : "text-primary")}>
                {connectIssue ? "Serial port not available" : "Before selecting COM port"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted">
                {connectIssue ? serialSetupAdvice(connectIssue) : "Use the Windows Bluetooth SPP Outgoing COM port. The backend Bluetooth consumer must be disabled while the browser bridge is reading."}
              </p>
            </div>
            <div className="grid gap-1.5 text-[11px] text-muted sm:grid-cols-2 lg:min-w-[28rem]">
              <span className="rounded-lg border border-border/50 bg-card/70 px-2.5 py-1">Bluetooth on</span>
              <span className="rounded-lg border border-border/50 bg-card/70 px-2.5 py-1">ESP32_SHT40 paired</span>
              <span className="rounded-lg border border-border/50 bg-card/70 px-2.5 py-1">Outgoing COM exists</span>
              <span className="rounded-lg border border-border/50 bg-card/70 px-2.5 py-1">FastAPI serial worker off</span>
            </div>
          </div>
          {connectIssue ? (
            <p className="mt-2 break-words rounded-lg border border-warning/20 bg-card/70 px-2.5 py-1.5 font-mono text-[11px] text-warning">
              browser error: {connectIssue}
            </p>
          ) : null}
        </div>
      </div>

      <div className="bg-[#07111f] px-4 py-3">
        <div
          ref={logContainerRef}
          className="max-h-56 min-h-32 overflow-y-auto rounded-xl border border-white/10 bg-[#050b14] px-3 py-2.5 font-mono text-[11px] leading-relaxed shadow-inner"
        >
          {!serialSupported ? <p className="text-error">[error] Web Serial is only available in supported desktop browsers such as Chrome or Edge.</p> : null}
          {serialSupported && !apiKey ? <p className="text-warning">[warning] Missing device provisioning API key.</p> : null}
          {logs.length === 0 ? <p className="text-white/45">[{new Date().toLocaleTimeString()}] Browser serial bridge idle.</p> : null}
          {logs.map((log) => (
            <p key={log.id} className={cn("break-words", webSerialLogStyle(log.state))}>
              [{log.at}] {log.message}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

export function useDataloggerConnections(deviceId: string) {
  const [connections, setConnections] = useState<ConnectionStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ items?: Array<Record<string, any>> }>("/connections", {
        params: { device_id: deviceId, limit: 50 },
      });

      let bufferDepth = 0;
      try {
        const bufRes = await fetch(`${API_BASE}/datalogger/buffer/status`);
        const bufData = await bufRes.json();
        bufferDepth = bufData.queue_depth ?? 0;
      } catch {
        /* non-critical */
      }

      const items: ConnectionStatusItem[] = (data.items ?? []).map((c: any) => {
        const lastSeen: string | null = c.last_seen_at ?? null;
        const secondsSince = lastSeen ? (Date.now() - new Date(lastSeen).getTime()) / 1000 : Infinity;
        const health: ConnectionHealth = !c.is_active ? "offline" : secondsSince < 120 ? "online" : secondsSince < 300 ? "degraded" : "offline";

        return {
          connectionId: c.id,
          label: c.label ?? c.connection_type,
          connectionType: c.connection_type,
          health,
          lastSeenAt: lastSeen,
          firmwareVersion: c.firmware_version ?? null,
          ipAddress: c.ip_address ?? null,
          pendingBufferCount: bufferDepth,
          batchAvailable: false,
        } satisfies ConnectionStatusItem;
      });

      if (mountedRef.current) setConnections(items);
    } catch (err) {
      console.error("useDataloggerConnections:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    void fetch_();
  }, [fetch_]);

  return { connections, loading, refresh: fetch_ };
}
